'use client';

import { useMemo } from 'react';
import { api } from '~/trpc/react';

import { useState } from 'react';
import { RouterOutputs } from '~/trpc/shared';
import { useFilter } from '~/utils/useFilter';
import { useTranslations } from 'next-intl';
import { useAtom } from 'jotai';
import { usePostHog } from 'posthog-js/react';
import { cartsAtom, cartOpenAtom, addToCartDateSelectionAtom } from '~/atoms';

import { Button } from '~/components/ui/button';
import { Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { calculateRentalPrice } from '~/utils/calculateRentalPrice';
import { isRangeOverlap } from 'range-overlap';

export default function AddToCart(props: { product: RouterOutputs['product']['search'][number]; isLarge?: boolean }) {
  const posthog = usePostHog();
  const utils = api.useContext();
  const [filter] = useFilter();
  const t = useTranslations();
  const [isLoading, setIsLoading] = useState(false);
  const [_, setIsOpen] = useAtom(cartOpenAtom);
  const [__, setDateSelectionOpen] = useAtom(addToCartDateSelectionAtom);
  const [cartIds, setCartIds] = useAtom(cartsAtom);
  const { data: carts } = api.cart.get.useQuery({});
  const _carts = !!carts ? carts : [];
  const lineItemQueries = api.useQueries((t) => cartIds.map((id) => t.cart.getLineItems({ cartId: id })));
  const addToCartMutation = api.cart.addToCart.useMutation();
  const fetchCartContents = api.cart.getAsync.useMutation();
  const createCartMutation = api.cart.create.useMutation();
  const datesAreSelected = !!filter?.dateRange?.from && !!filter?.dateRange?.to;

  const isProductInCart = () => {
    let isAlreadyInCart = false;

    lineItemQueries?.forEach((query) => {
      if (!query.data) return;

      query.data?.forEach((lineItem) => {
        if (lineItem.product?.id !== props.product.product.id) return;
        const currentCart = _carts.find((cart) => cart.cart.id === lineItem.line_item.cartId);
        if (!currentCart) return;
        if (
          currentCart.cart.merchantId === props.product.product.merchantId &&
          isRangeOverlap(
            [currentCart.cart.rentalStartDate, currentCart.cart.rentalEndDate],
            [filter.dateRange.from, filter.dateRange.to]
          )
        ) {
          isAlreadyInCart = true;
        }
      });
    });
    return isAlreadyInCart;
  };

  const addToCart = async () => {
    setIsLoading(true);
    // check if cart with this merchant and period exists
    const carts = await fetchCartContents.mutateAsync({ cartIds: cartIds });
    const existingCartId = carts?.find((cart) => {
      return (
        cart.cart.merchantId === props.product.product.merchantId &&
        cart.cart.rentalStartDate.getTime() === filter.dateRange.from.getTime() &&
        cart.cart.rentalEndDate.getTime() === filter.dateRange.to.getTime()
      );
    });
    const price = calculateRentalPrice(
      filter.dateRange,
      {
        dailyRentalPrice: props.product.product.dailyRentalPrice,
        weeklyRentalPrice: props.product.product.weeklyRentalPrice,
        monthlyRentalPrice: props.product.product.monthlyRentalPrice
      },
      props.product.merchant?.taxRate || 0
    );

    if (!!existingCartId) {
      // if exists, add product to cart
      const cartId = existingCartId.cart.id;

      try {
        await addToCartMutation.mutateAsync({
          cartId: cartId,
          productId: props.product.product.id,
          name: props.product.product.name,
          subTotal: price.subTotal,
          discount: price.discount,
          tax: price.tax,
          total: price.total
        });
        utils.cart.get.invalidate();
        utils.cart.getLineItems.invalidate();
        setIsOpen(true);
        setIsLoading(false);
      } catch (e) {
        console.error('Cart exists, but error adding to cart: ', e);
        toast.error('Error adding product to cart. Please contact support.');
      }
      return;
    }

    // if doesn't exist, create new cart
    const newCart = await createCartMutation.mutateAsync({
      merchantId: props.product.product.merchantId,
      rentalStartDate: filter.dateRange.from,
      rentalEndDate: filter.dateRange.to
    });
    setCartIds([...cartIds, newCart]);

    try {
      await addToCartMutation.mutateAsync({
        cartId: newCart,
        productId: props.product.product.id,
        name: props.product.product.name,
        subTotal: price.subTotal,
        discount: price.discount,
        tax: price.tax,
        total: price.total
      });
      utils.cart.get.invalidate();
      utils.cart.getLineItems.invalidate();
      setIsOpen(true);
      setIsLoading(false);
    } catch (e) {
      console.error("Cart doesn't exist, error adding to cart: ", e);
      toast.error('Error adding product to cart. Please contact support.');
    }
    setDateSelectionOpen({
      isOpen: false,
      product: null
    });

    posthog.capture('added product to cart', {
      $set: { product: props.product.product.name, total: price.total }
    });
  };

  return (
    <Button
      disabled={!datesAreSelected || isProductInCart() || isLoading}
      onClick={addToCart}
      className={`w-full cursor-pointer ${!!props?.isLarge && 'h-12'}`}
    >
      {isLoading ? (
        <Loader2 size={18} className="mr-2 motion-safe:animate-spin" />
      ) : (
        <>
          {isProductInCart() ? (
            <>
              <Check size={18} className="mr-2" />
              {t('addedToBooking')}
            </>
          ) : (
            t('addToBooking')
          )}
        </>
      )}
    </Button>
  );
}
